import { apiClient } from './apiClient'
import { calculatePrayerTimes, getTimezoneOffset, type PrayerTimes as CalculatedPrayerTimes } from './prayerTimesCalculator'

export interface City {
  id: string | number // Can be string (Guid) or number
  name: string
  nameEn: string
  latitude: number
  longitude: number
}

export interface PrayerTime {
  name: string
  time: string
  nameEn: string
}

export interface PrayerTimesResponse {
  date: string
  prayers: PrayerTime[]
  hijriDate?: string
  timezone?: string
  method?: string
}

export interface QiblaDirection {
  latitude: number
  longitude: number
  direction: number // Degrees from North
}

export interface CalculationMethod {
  id: number
  name: string
  nameAr: string
}

export const CALCULATION_METHODS: CalculationMethod[] = [
  { id: 2, name: 'Islamic Society of North America (ISNA)', nameAr: 'الجمعية الإسلامية لأمريكا الشمالية' },
  { id: 3, name: 'Muslim World League', nameAr: 'رابطة العالم الإسلامي' },
  { id: 5, name: 'Egyptian General Authority', nameAr: 'الهيئة العامة المصرية للمساحة' },
  { id: 4, name: 'Umm Al-Qura University, Makkah', nameAr: 'جامعة أم القرى، مكة' },
  { id: 1, name: 'University of Islamic Sciences, Karachi', nameAr: 'جامعة العلوم الإسلامية، كراتشي' },
  { id: 9, name: 'Kuwait', nameAr: 'الكويت' },
  { id: 10, name: 'Qatar', nameAr: 'قطر' },
  { id: 16, name: 'Dubai', nameAr: 'دبي' },
  { id: 13, name: 'Turkey (Diyanet)', nameAr: 'تركيا' },
]

export const prayerTimesApi = {
  async getCities(): Promise<City[]> {
    try {
      const response = await apiClient.get<City[]>('/PrayerTimes/cities')
      return response.data
    } catch (error) {
      console.warn('Failed to fetch cities from API, using fallback cities', error)
      // Fallback cities
      return [
        { id: '1', name: 'القاهرة', nameEn: 'Cairo', latitude: 30.0444, longitude: 31.2357 },
        { id: '2', name: 'الإسكندرية', nameEn: 'Alexandria', latitude: 31.2001, longitude: 29.9187 },
        { id: '3', name: 'الرياض', nameEn: 'Riyadh', latitude: 24.7136, longitude: 46.6753 },
        { id: '4', name: 'جدة', nameEn: 'Jeddah', latitude: 21.4858, longitude: 39.1925 },
        { id: '5', name: 'مكة المكرمة', nameEn: 'Mecca', latitude: 21.3891, longitude: 39.8579 },
      ]
    }
  },

  async getPrayerTimes(cityId: number | string, date?: Date): Promise<PrayerTimesResponse> {
    const targetDate = date || new Date()
    
    // Fetch from API only - no fallback
    const dateParam = targetDate ? `?date=${targetDate.toISOString().split('T')[0]}` : ''
    const response = await apiClient.get<PrayerTimesResponse>(`/PrayerTimes/${cityId}/times${dateParam}`)
    return response.data
  },

  async getPrayerTimesByCoordinates(
    latitude: number,
    longitude: number,
    date?: Date,
    method: number = 5
  ): Promise<PrayerTimesResponse> {
    const targetDate = date || new Date()
    
    // Fetch from API only - no fallback
    const response = await apiClient.get<PrayerTimesResponse>(
      `/PrayerTimes/by-coordinates?latitude=${latitude}&longitude=${longitude}&date=${targetDate.toISOString().split('T')[0]}&method=${method}`
    )
    return response.data
  },

  async getQiblaDirection(latitude: number, longitude: number): Promise<QiblaDirection> {
    try {
      const response = await apiClient.get<QiblaDirection>(
        `/PrayerTimes/qibla?latitude=${latitude}&longitude=${longitude}`
      )
      return response.data
    } catch (error) {
      console.error('Failed to fetch Qibla direction', error)
      
      // Fallback: Calculate Qibla direction to Mecca (21.4225, 39.8262)
      const meccaLat = 21.4225
      const meccaLon = 39.8262
      
      const dLon = ((meccaLon - longitude) * Math.PI) / 180
      const lat1 = (latitude * Math.PI) / 180
      const lat2 = (meccaLat * Math.PI) / 180
      
      const y = Math.sin(dLon) * Math.cos(lat2)
      const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)
      
      let bearing = Math.atan2(y, x)
      bearing = (bearing * 180) / Math.PI
      bearing = (bearing + 360) % 360
      
      return {
        latitude,
        longitude,
        direction: bearing,
      }
    }
  },

  async getCurrentLocation(): Promise<{ latitude: number; longitude: number }> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        },
        (error) => {
          reject(error)
        }
      )
    })
  },
}

